import { useToast } from "@chakra-ui/react";
import { OrganisationFeatureFlag } from "@tether-web-portal/types/organisationFeatureFlag";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { api } from "../api";
import { FieldUserProperty } from "../../types/FieldUserProperty";
import { Organisation } from "../../types/Organisation";
import { Property } from "../../types/Property";
import { OrgDeviceCounts } from "@tether-web-portal/types/DeviceCounts";
import { PropertyDeviceTypes } from "@tether-web-portal/types/PropertyDeviceTypes";
import { deviceCountsToDeviceTypes } from "@tether-web-portal/utils/deviceCountsToDeviceTypes";
import { useAuth } from "../../auth/useAuth";
import {
  applySearchFallback,
  applySortFallback,
  ListResult,
  Page,
  pageQueryParams,
  unwrapListResponse,
  WHOLE_LIST_FALLBACK,
} from "../pagination";

export enum OrganisationQueryKeys {
  Organisations = "organisations",
  MemberOrganisations = "member-organisations",
  FieldUsers = "organisation-field-users",
  FeatureFlags = "organisation-feature-flags",
  Settings = "organisation-settings",
  OrganisationDeviceCounts = "organisation-device-counts",
}

/**
 * One organisation, by id — the way to get the CURRENT organisation's name or record.
 *
 * The API answers only for an organisation the signed-in user belongs to. Disabled until
 * there is an id, so a caller holding `user?.currentOrganisationId` before sign-in settles
 * does not ask for `v2/organisation/undefined`.
 */
export const useGetOrganisation = (id: string | undefined) => {
  const { data: organisation, ...rest } = useQuery<Organisation>(
    [OrganisationQueryKeys.Organisations, id],
    () => api.get(`v2/organisation/${id}`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
    }
  );
  return { organisation, ...rest };
};

/** How many organisations the switcher asks for at a time. */
export const ORGANISATION_PICKER_PAGE_SIZE = 50;

/** Accents off and lower-cased, so the browser-side search matches "Kāinga" from "kainga". */
const foldForSearch = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export interface SearchOrganisationsParams {
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * ONE page of the organisations the signed-in user belongs to, by name, with the total.
 *
 * This replaced `useGetOrganisations`, which downloaded every membership to show a switcher
 * and to find the current organisation's name in it. Tether's support user belongs to every
 * organisation on the platform, so that was thousands of records on every page view.
 *
 * `GET /account/v2/organisation` answers two shapes. With paging parameters and
 * ORG_SCALE_PAGINATION on it pages, sorts by name and searches name and id on the server.
 * Otherwise (flag off, or an API from before the route was paged) it ignores them and
 * answers with every membership, unordered — so here that array is sorted by name, searched
 * and windowed as the server would have, and callers see the same thing either way.
 *
 * `fullMembersOnly` is deliberately not sent: this portal has always listed every
 * membership, field-user ones included.
 */
export const useSearchOrganisations = (
  { search = "", limit = ORGANISATION_PICKER_PAGE_SIZE, offset = 0 }: SearchOrganisationsParams,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const term = search.trim();

  const { data, ...rest } = useQuery<Organisation[] | Page<Organisation>>(
    // Keyed on the user: the list is the user's memberships, and app-wide refetching is off.
    [OrganisationQueryKeys.MemberOrganisations, userId, { search: term, limit, offset }],
    async () => {
      // A legacy whole array (flag off) is held once per user, once it has ARRIVED, and every
      // later search narrows that copy: without this each settled search downloaded every
      // membership again. Only a completed array is reused - never a request in flight - so a
      // search begun while another is pending sends its own request and gets its own page,
      // not the previous term's (fetchListSharingFallback joins in-flight requests by key).
      const shareKey = [OrganisationQueryKeys.MemberOrganisations, WHOLE_LIST_FALLBACK, userId];
      const held = queryClient.getQueryState<Organisation[]>(shareKey);
      if (held?.data && !held.isInvalidated) return held.data;
      const answer = await api.get<Organisation[] | Page<Organisation>>(
        "/account/v2/organisation",
        pageQueryParams({ limit, offset, sort: "name", search: term || undefined })
      );
      if (Array.isArray(answer)) queryClient.setQueryData(shareKey, answer);
      return answer;
    },
    {
      enabled: enabled && Boolean(userId),
      // Each settled keystroke is a new key; the rows of the last answer stay (dimmed by the
      // caller) until the next one lands, instead of the list emptying between searches.
      keepPreviousData: true,
      staleTime: 60_000,
    }
  );

  const result = unwrapListResponse<Organisation>(data);
  if (result.serverPaged) {
    return { ...rest, ...result };
  }

  // The legacy whole list: sort, search and window it here. A membership whose
  // organisation has gone comes back as null in that array; it has nothing to show.
  const present: ListResult<Organisation> = { ...result, items: result.items.filter(Boolean) };
  // Accents folded for the order too, as the column's collation folds them.
  const sorted = applySortFallback(
    present,
    "name",
    false,
    (o) => (typeof o.name === "string" ? foldForSearch(o.name) : undefined),
    (o) => o.id
  );
  const searched = applySearchFallback(
    sorted,
    term ? foldForSearch(term) : undefined,
    (o) => [typeof o.name === "string" ? foldForSearch(o.name) : undefined, o.id],
    // Uncapped: the window is cut below, at the offset asked for.
    Number.MAX_SAFE_INTEGER
  );

  return {
    ...rest,
    items: searched.items.slice(offset, offset + limit),
    // Every match, not the window: the caller says "50 of 214".
    total: searched.total,
    serverPaged: false,
  };
};

export const useGetFieldUserProperties = (organisationId: string, userId: string) => {
  const { data: properties, ...rest } = useQuery<FieldUserProperty[]>(
    [OrganisationQueryKeys.FieldUsers, organisationId, userId],
    () => api.get(`account/v2/fielduserassignment/?organisationId=${organisationId}&userId=${userId}`),
    {
      staleTime: Infinity,
    }
  );

  return { properties, ...rest };
};

export const useUpdateFieldUserPropertiesMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleUpdateFieldUser = async ({
    userId,
    existingProperties,
    newProperties,
  }: {
    userId: string;
    // Only `id` is read, so a caller that has ids does not have to fetch whole properties
    // to hand them over.
    existingProperties: Pick<Property, "id">[];
    newProperties: Pick<Property, "id">[];
  }) => {
    // Compared by ID, not by object identity. `includes` only worked because both lists
    // were filtered out of the SAME array, so the entries were the same objects — the
    // moment a caller builds either list from ids instead, every existing property looks
    // removed and the user loses all their assignments.
    const newPropertyIds = new Set((newProperties ?? []).map((property) => property.id));
    const propertiesToBeRemoved = existingProperties?.filter(
      (property) => !newPropertyIds.has(property.id)
    );

    try {
      const addPropertyPromises =
        newProperties && newProperties.length > 0
          ? newProperties.map((property) => {
              return api.post(`account/v2/fielduserassignment/`, { userId, installationId: property.id });
            })
          : [];
      const deletePropertyPromises =
        propertiesToBeRemoved && propertiesToBeRemoved.length > 0
          ? propertiesToBeRemoved.map(async (property) => {
              return await api.del(
                `account/v2/fielduserassignment/?installationId=${property.id}&userId=${userId}`
              );
            })
          : [];
      await Promise.all(addPropertyPromises.concat(deletePropertyPromises));
    } catch (e) {
      console.log("failed to update Field User");
    }
  };

  return useMutation(handleUpdateFieldUser, {
    onError: () => {
      toast({
        title: "Unable to updated Restricted User",
        status: "error",
        isClosable: true,
        position: "top-right",
      });
      queryClient.invalidateQueries([OrganisationQueryKeys.FieldUsers]);
    },

    onSuccess: () => {
      toast({
        title: "Successfully updated field user",
        status: "success",
        isClosable: true,
        position: "top-right",
      });

      queryClient.invalidateQueries([OrganisationQueryKeys.FieldUsers]);
    },
  });
};

const useGetOrganisationFeatureFlags = (enabled = true) => {
  return useQuery<OrganisationFeatureFlag[]>(
    [OrganisationQueryKeys.FeatureFlags],
    () => api.get(`v2/organisationfeatureflag`),
    { enabled: enabled }
  );
};

export const useGetOrganisationFeatureFlag = (featureFlag: string, enabled = true) => {
  const {
    data: featureFlags,
    isLoading: featureFlagsLoading,
    isError: featureFlagsError,
    isFetching: featureFlagsFetching,
  } = useGetOrganisationFeatureFlags(enabled);

  const isEnabled = useMemo(() => {
    return (featureFlags ?? []).find((x) => x.name === featureFlag)?.value ?? false;
  }, [featureFlags, featureFlag]);

  return {
    value: isEnabled,
    isLoading: featureFlagsLoading,
    error: featureFlagsError,
    isFetching: featureFlagsFetching,
  };
};

export const useGetOrganisationSetting = (
  settingName: string,
  options?: UseQueryOptions<number | string | boolean, Error>
) => {
  const { data: setting, ...rest } = useQuery<number | string | boolean, Error>(
    [OrganisationQueryKeys.Settings, settingName],
    () => api.get(`v2/organisationsetting/${settingName}`).then((r) => r.value),
    options
  );
  return { setting, ...rest };
};

export const useManageAccountDataMutation = () => {
  const toast = useToast();

  const sendRequest = (requestType: "delete" | "export") => {
    return api.post(`account/v2/managedata/${requestType}`);
  };

  return useMutation(sendRequest, {
    onError: () => {
      toast({
        title: "Unable to submit request",
        status: "error",
        isClosable: true,
        position: "top-right",
      });
    },

    onSuccess: () => {
      toast({
        title: "Request was successfully submitted",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    },
  });
};

export const useGetOrganisationDeviceCounts = (id?: string) => {
  const { data: orgDeviceCounts, ...rest } = useQuery<OrgDeviceCounts, Error>(
    [OrganisationQueryKeys.OrganisationDeviceCounts, id],
    () => api.get(`experience/v1/organisation/${id}/device-counts`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
    },
  );

  const orgDeviceTypes = useMemo((): PropertyDeviceTypes => {
    if (!orgDeviceCounts) return new Set();

    const { total, properties } = orgDeviceCounts;
    let propertyDeviceTypes: PropertyDeviceTypes = deviceCountsToDeviceTypes(total);
    for (const propertyId in properties) {
      propertyDeviceTypes = new Set([
        ...propertyDeviceTypes,
        ...deviceCountsToDeviceTypes(properties[propertyId]),
      ]);
    }
    return propertyDeviceTypes;
  }, [orgDeviceCounts]);
  return { orgDeviceCounts, orgDeviceTypes, ...rest };
};


export const useUpdateOrganisationSettingMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    async ({
      organisationId,
      name,
      value,
    }: {
      organisationId: string;
      name: string;
      value: number | string | boolean;
    }) => {
      return api.put(`v2/organisationsetting/${name}`, {
        organisationId,
        name,
        value,
      });
    },
    {
      onSuccess: () => {
        toast({
          title: "Setting updated.",
          status: "success",
          position: "top-right",
        });

        // Invalidate settings everywhere
        queryClient.invalidateQueries([OrganisationQueryKeys.Settings]);
      },
      onError: (error: any) => {
        toast({
          title: "Failed to update setting",
          description: error?.message,
          status: "error",
          position: "top-right",
        });
      },
    }
  );
};

